import {ElementRef, EventEmitter, inject, Injectable, Input, OnInit, Output} from "@angular/core";

import { isArray, isEmpty, isNull, find, isEqual, toString, isObject, split, isString, cloneDeep, forEach, trim, isUndefined, includes, get, uniqBy,
  uniqWith, filter, isEqualWith, every, map, orderBy, indexOf } from "lodash-es";
import { Subject } from "rxjs";

import { ToDatePipe } from "@wm/components-base";
import { $parseEvent, $parseExpr, isDefined, toBoolean} from "@wm/core";

import { BaseComponent } from "@wms/components";
import { convertDataToObject, extractDataAsArray, getOrderedDataset, getUniqObjsByDataField, isEqualWithFields,
  transformDataWithKeys, transformFormData } from "./form-utils";

/**
 * key represents the datafield value
 * label represents display value or expression value
 * value displayValue for primitives and data object for allFields
 * dataObject represent the object from the dataset when datafield is ALLFIELDS. This is used as innerItem while grouping the datasetItems.
 * imgSrc picture source
 * selected represents boolean to notify selected item.
 */
export class DataSetItem {
  key: any;
  label: any;
  value: any;
  dataObject?: Object;
  index?: number;
  imgSrc?: string;
  selected?: boolean;
}

const ALLFIELDS = 'All Fields';
const isSearchWidget = (widgetType: any) => {
  return widgetType === 'wm-search';
};

@Injectable()
export class DatasetAwareFormComponent extends BaseComponent {
  @Input() datafield: string = '';
  @Input() displayfield: string = '';

  @Output()
  onChange: EventEmitter<{ value: any, $event?: Event | any, valid?: boolean }> = new EventEmitter();
  public datasetItems: DataSetItem[] = [];
  public groupby!: string;
  public appDefaults: any;
  public orderby!: string;
  public usekeys!: boolean;
  public displayexpression!: string;
  public displaylabel!: string;
  public displayimagesrc!: string;
  public allowempty!: boolean;
  public acceptsArray!: Boolean;
  public _modelByValue: any;
  public compareby!: string;
  public multiple: Boolean = true;
  public nativeElement: HTMLElement;
  public datePipe: any;
  expressionValue: boolean = true;
  protected match!: string;
  protected dateformat!: string;
  protected _modelByKey: any;
  private _dataset: any;

  constructor() {
    super();
    const elementRef = inject(ElementRef);
    this.nativeElement = elementRef.nativeElement;
  }

  get modelByKey(): any {
    return this._modelByKey;
  }

  set modelByKey(val: any) {
    this.selectByKey(val);
    this.onChange.emit({value: this._modelByValue});
  }

  get datavalue() {
    return this._modelByValue;
  }

  @Input() set datavalue(val: any) {
    if (this.multiple) {
      val = extractDataAsArray(val);
    }
    this._modelByValue = val;

    this.selectByValue(val);
  }

  get dataset() {
    return this._dataset;
  }

  @Input() set dataset(val: any) {
    this._dataset = val;
    this.initDatasetItems();
  }

  protected initDatasetItems() {
    if (!this.dataset || isEmpty(this.dataset)) {
      this.datasetItems = [];
      return;
    }
    const orderedDataset = getOrderedDataset(convertDataToObject(this.dataset), this.orderby);

    if (this.usekeys) {
      this.datasetItems = transformDataWithKeys(orderedDataset);
    } else {
      const displayOption = transformFormData({}, orderedDataset, this.datafield, {
        displayField: this.displayfield || this.displaylabel,
        displayExpr: this.displayexpression,
        displayImgSrc: this.displayimagesrc
      });
      // @ts-ignore
      this.datasetItems = [...getUniqObjsByDataField(displayOption, this.datafield, this.displayfield || this.displaylabel, toBoolean(this.allowempty))];

    }
    this.postDatasetItemsInit();
  }

  protected postDatasetItemsInit() {
      this.selectByValue(this.datavalue);
  }

  protected resetDatasetItems() {
    this.datasetItems.forEach(item => item.selected = false);
  }

  protected selectByValue(values: Array<any> | any) {
    this.resetDatasetItems();

    // if datavalue is not defined or empty then set the model as undefined.
    if (!isDefined(values) || values === '' || isNull(values) || (values instanceof Array && !values.length)) {
      this._modelByKey = undefined;
      // do not return when allowempty is set to true.
      if (!this.allowempty || !isDefined(values)) {
        return;
      }
    }

    if (this.acceptsArray && !isArray(values)) {
      values = this.allowempty ? [values] : extractDataAsArray(values);
    }

    const filterField = this.datafield === ALLFIELDS ? 'dataObject' : 'key';

    if (isArray(values)) {
      this._modelByKey = [];
      values.forEach(val => {
        const itemByValue = find(this.datasetItems, item => {
          if (filterField === 'dataObject') {
            if (this.compareby && this.compareby.length) {
              return isEqualWithFields(item[filterField], val, this.compareby);
            }
          }
          return (isObject(item.value) ? isEqual(item.value, val) : (toString(item.value)).toLowerCase() === (toString(val)).toLowerCase());
        });
        if (itemByValue) {
          itemByValue.selected = true;
          this._modelByKey.push(itemByValue.key);
        }
      });
    } else {
      this._modelByKey = undefined;
      const itemByValue = find(this.datasetItems, item => {
        if (filterField === 'dataObject') {
          if (this.compareby && this.compareby.length) {
            return isEqualWithFields(item[filterField], values, this.compareby);
          }
        }
        return (isObject(item.value) ? isEqual(item.value, values) : (toString(item.value)).toLowerCase() === (toString(values)).toLowerCase());
      });
      if (itemByValue) {
        itemByValue.selected = true;
        this._modelByKey = itemByValue.key;
      }
    }
  }

  protected selectByKey(val: any): any {
    this.resetDatasetItems();

    if (!this.datasetItems.length) {
      return;
    }

    if (this.multiple && !isArray(val)) {
      val = [val];
    }

    // Set the _modelByKey to the modified keys.
    this._modelByKey = val;

    if (this.multiple) {
      this._modelByValue = [];
      val.forEach((key: any) => {
        const itemByKey = find(this.datasetItems, item => {
          // not triple equal, as the instance type can be different.
          // only value comparison should be done.
          return toString(item.key) === toString(key);
        });
        if (itemByKey) {
          itemByKey.selected = true;
          this._modelByValue = [...this._modelByValue, itemByKey.value];
        }
      });
    } else {
      this._modelByValue = '';
      const itemByKey = find(this.datasetItems, item => {
        // not triple equal, as the instance type can be different.
        // only value comparison should be done.
        return toString(item.key) === toString(val);
      });
      if (itemByKey) {
        itemByKey.selected = true;
        this._modelByValue = itemByKey.value;
      }
    }
  }
}
